import { Box, Typography } from "@jewlr/storybook/core"
import { useRef } from "react"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"
import styled from "styled-components"

import {
  Content,
  SearchBlock,
  SearchFlex,
  SearchBox,
  SubTitle,
  TitleBorder,
} from "areas/helpdesk/index"
import { trimToLastSentence } from "helpers/application"
import scrollToRef from "helpers/scroll-to-ref"
import { handleLiveChat } from "live-chat"

import getContactData from "./data"

const SectionTitle = styled(Typography).attrs((props) => ({
  color: "black",
  fontSize: 24,
  my: 2,
  px: 4,
  uppercase: true,
  ...props.theme.helpdesk.sectionTitle,
}))``

const ContactLink = styled(Typography).attrs((props) => ({
  color: "primary",
  fontSize: 16,
  ...props,
  ...props.theme.helpdesk.contactLink,
}))`
  text-decoration: ${(props) => props.theme.helpdesk.contact.textDecoration};
  &:focus {
    color: ${(props) => props.theme.colors.primary};
  }
`

const Contact = () => {
  const productionRef = useRef()
  const receivedRef = useRef()
  const contactData = getContactData()
  return (
    <Box textAlign="center">
      <Helmet>
        {/* eslint-disable sort-keys */}
        <title>How can we help you?</title>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: Object.values(contactData)
              .map((category) => ({
                ...category,
                articles: category.articles.map((article) => ({
                  ...article,
                  title: `${category.title}, ${article.title}`,
                })),
              }))
              .flatMap((category) => category.articles)
              .map((article) => ({
                "@type": "Question",
                name: article.title,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: trimToLastSentence(article.text),
                },
              })),
          })}
        </script>
        {/* eslint-enable sort-keys */}
      </Helmet>

      <TitleBorder>How can we help you?</TitleBorder>
      <Content mx={3} my={4}>
        Welcome, do you require assistance? We want to help you! If your order
        is in production and you want to make a change, please{" "}
        <ContactLink
          as="a"
          color="primary"
          fontSize={16}
          href="#"
          onClick={(e) => {
            e.preventDefault()
            scrollToRef(productionRef)
          }}
        >
          click here
        </ContactLink>
        . If you have received your order and need to make a change, please
        <ContactLink
          as="a"
          color="primary"
          fontSize={16}
          href="#"
          onClick={(e) => {
            e.preventDefault()
            scrollToRef(receivedRef)
          }}
        >
          {" "}
          click here
        </ContactLink>{" "}
        . If you do not find what you&apos;re looking for, please read our{" "}
        <ContactLink as={Link} data-lc="contact" to="/helpdesk">
          Frequently Asked Questions page
        </ContactLink>
      </Content>

      <SectionTitle id="in_production" ref={productionRef}>
        {contactData.production.title.toUpperCase()}…
      </SectionTitle>

      <SearchFlex addYPadding>
        {contactData.production.articles.map((article, index) => {
          return (
            <SearchBlock key={`production-${article.title}-${index}`}>
              <SearchBox my={{ _: 1, tablet: 0 }} px={3} py={4} simple>
                <SubTitle as={Link} data-lc="contact" to={article.link}>
                  {article.title}
                </SubTitle>
                <Content>
                  {article.text}
                  <ContactLink as={Link} data-lc="contact" to={article.link}>
                    click here
                  </ContactLink>
                </Content>
              </SearchBox>
            </SearchBlock>
          )
        })}
      </SearchFlex>
      <SectionTitle id="order_received" ref={receivedRef}>
        {contactData.received.title.toUpperCase()}…
      </SectionTitle>
      <SearchFlex addYPadding>
        {contactData.received.articles.map((article, index) => {
          return (
            <SearchBlock key={`received-${article.title}-${index}`}>
              <SearchBox my={{ _: 1, tablet: 0 }} px={3} py={4} simple>
                <SubTitle as={Link} data-lc="contact" to={article.link}>
                  {article.title}
                </SubTitle>
                <Content>
                  {article.text}
                  <ContactLink as={Link} data-lc="contact" to={article.link}>
                    click here
                  </ContactLink>
                </Content>
                <Content>
                  {article.secondText}
                  {article.secondText && article.secondTextLink ? (
                    <ContactLink
                      as={Link}
                      data-lc="contact"
                      to={article.secondTextLink}
                    >
                      click here
                    </ContactLink>
                  ) : (
                    ""
                  )}
                </Content>
              </SearchBox>
            </SearchBlock>
          )
        })}
      </SearchFlex>

      <SectionTitle>{contactData.assistance.title.toUpperCase()}…</SectionTitle>
      <SearchFlex addYPadding>
        <SearchBlock>
          <SearchBox my={{ _: 1, tablet: 0 }} px={3} py={4} simple>
            <SubTitle>How Do I Contact {sl.config.site_title}?</SubTitle>
            <Content>
              If you need further assistance, please send us an{" "}
              <ContactLink as={Link} data-lc="contact" to="/contact_form">
                email
              </ContactLink>{" "}
              , including your order number and a summary of your concern. Our
              customer support agents will be happy to assist you.
              <br />
              If you would like to speak to one of our live agents, we are
              available Monday to Sunday, 9:00 am–5:30 pm EST, via{" "}
              <ContactLink
                as="a"
                onClick={() => {
                  handleLiveChat()
                }}
                pointer
              >
                LiveChat
              </ContactLink>{" "}
            </Content>
          </SearchBox>
        </SearchBlock>
      </SearchFlex>
    </Box>
  )
}

export default Contact
